import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button, Flex, Table, Tooltip, message, Spin, Select } from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { SearchInput } from '@/components/SearchInput'
import Title from 'antd/es/typography/Title'
import { type Role, type User } from '@/data'
import { getRoles } from '@/api/roles/getRoles'
import { getUsers } from '@/api/users/getUsers'
import { deleteUser } from '@/api/users/deleteUser'
import { updateUser } from '@/api/users/updateUser'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'
import { getErrorMessage } from '@/utils/getErrorMessage'
import {
  sortOptionsByLabel,
  sortSelectedValues,
  type SelectOption,
} from '@/utils/selectSorting'

export const UsersList: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!
  const [users, setUsers] = useState<User[]>([])
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([])
  const [loading, setLoading] = useState(true)
  const [savingRoleIds, setSavingRoleIds] = useState<number[]>([])
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  })
  const [search, setSearch] = useState('')

  const hasFetched = useRef(false)
  const canReadUsers = hasPermission(auth.user, 'lop:ui:action:read:users')
  const canCreateUsers = hasPermission(auth.user, 'lop:ui:action:create:users')
  const canUpdateUsers = hasPermission(auth.user, 'lop:ui:action:update:users')
  const canDeleteUsers = hasPermission(auth.user, 'lop:ui:action:delete:users')

  const fetchRoleOptions = useCallback(async () => {
    const { data } = await getRoles()
    setRoleOptions(
      sortOptionsByLabel(
        data.map((role: Role) => ({
          label: role.name,
          value: role.id,
        })),
      ),
    )
  }, [])

  const fetchUsers = useCallback(
    async (page = 1, query = search) => {
      try {
        setLoading(true)
        setError(null)
        const [{ data, meta }] = await Promise.all([
          getUsers({
            page,
            ...(query ? { filters: [{ key: 'name', value: query }] } : {}),
          }),
          fetchRoleOptions(),
        ])
        setUsers(data)
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch (err) {
        setError('Failed to load users')
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [fetchRoleOptions, search],
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
      fetchUsers(1, value)
    },
    [fetchUsers],
  )

  useEffect(() => {
    if (hasFetched.current) return
    if (!canReadUsers) return
    hasFetched.current = true
    fetchUsers()
  }, [canReadUsers, fetchUsers])

  const handleDelete = async (id: number) => {
    try {
      await deleteUser(String(id))
      message.success('User deleted')
      fetchUsers(pagination.current)
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to delete user'))
      console.error(err)
    }
  }

  const handleRolesChange = async (id: number, roleIds: number[]) => {
    try {
      const sortedRoleIds = sortSelectedValues(roleIds, roleOptions)
      setSavingRoleIds((current) => [...current, id])
      const updatedUser = await updateUser(String(id), {
        role_ids: sortedRoleIds,
      })
      setUsers((current) =>
        current.map((user) =>
          user.id === id
            ? {
                ...updatedUser,
                roleIds: sortSelectedValues(updatedUser.roleIds, roleOptions),
              }
            : user,
        ),
      )
      message.success('Roles updated')
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to update roles'))
      console.error(err)
    } finally {
      setSavingRoleIds((current) => current.filter((value) => value !== id))
    }
  }

  const columns: TableProps<User>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) =>
        canUpdateUsers ? (
          <Link to={`/users/${record.id}/edit`}>{text}</Link>
        ) : (
          text
        ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      render: (_, record) => (
        <Select<number[]>
          mode="multiple"
          value={sortSelectedValues(record.roleIds, roleOptions)}
          options={roleOptions}
          onChange={(value) => handleRolesChange(record.id, value)}
          loading={savingRoleIds.includes(record.id)}
          disabled={
            !canUpdateUsers ||
            savingRoleIds.includes(record.id) ||
            roleOptions.length === 0
          }
          placeholder="Select roles"
          optionFilterProp="label"
          style={{ minWidth: 220 }}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Flex gap="middle">
          {canUpdateUsers ? (
            <Tooltip title="Edit">
              <Link to={`/users/${record.id}/edit`}>
                <EditOutlined style={{ fontSize: 16 }} />
              </Link>
            </Tooltip>
          ) : null}
          {canDeleteUsers ? (
            <Tooltip title="Delete">
              <DeleteOutlined
                style={{ fontSize: 16, color: '#ff4d4f', cursor: 'pointer' }}
                onClick={() => handleDelete(record.id)}
              />
            </Tooltip>
          ) : null}
        </Flex>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <Title level={4}>Users</Title>
        <p style={{ color: '#ff4d4f' }}>{error}</p>
        <Button onClick={() => fetchUsers()}>Retry</Button>
      </div>
    )
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Users
        </Title>
        <Flex gap="small" align="center">
          <SearchInput onSearch={handleSearch} />
          {canCreateUsers ? (
            <Button type="primary" onClick={() => navigate('/users/new')}>
              New
            </Button>
          ) : null}
        </Flex>
      </Flex>
      {canReadUsers ? (
        <Spin spinning={loading}>
          <Table<User>
            rowKey="id"
            columns={columns}
            dataSource={users}
            pagination={pagination}
            onChange={(p) => fetchUsers(p.current, search)}
          />
        </Spin>
      ) : null}
    </div>
  )
}
